// Read-only aggregated snow reports for a ski location (last 12 hours). Counts only: no
// names, no user ids and no per-report times. Below 3 reports only the total is shown.
// Works as a Server Component; pass the result of readSnowCheckinSummary() or
// GET /api/snow-checkins?locationId=.

import type { SurfaceTag } from "@/lib/conditions/ski/types";
import {
  SNOW_CHECKIN_COVERS,
  SNOW_CHECKIN_COVER_LABELS,
  SNOW_CHECKIN_SURFACES,
  SNOW_CHECKIN_SUMMARY,
  SNOW_CHECKIN_SURFACE_LABELS,
  type SnowCheckinPublicSummary,
} from "@/lib/conditions/snow-checkins";

const COVER_BAR: Readonly<Record<(typeof SNOW_CHECKIN_COVERS)[number], string>> = {
  full: "bg-emerald-500",
  thin: "bg-lime-400",
  patchy: "bg-amber-400",
  bare: "bg-red-400",
};

/** Up to three most reported surfaces, most common first (ties keep the display order). */
export function topSnowCheckinSurfaces(summary: SnowCheckinPublicSummary, limit = 3): { surface: SurfaceTag; count: number }[] {
  return SNOW_CHECKIN_SURFACES
    .map((surface) => ({ surface, count: summary.surface[surface] }))
    .filter((entry) => entry.count > 0)
    .sort((a, b) => b.count - a.count)
    .slice(0, limit);
}

export function SnowCheckinSummary({ summary }: { summary: SnowCheckinPublicSummary }) {
  const coverTotal = SNOW_CHECKIN_COVERS.reduce((sum, cover) => sum + summary.cover[cover], 0);
  const surfaces = topSnowCheckinSurfaces(summary);
  const reports = `${summary.total}${summary.truncated ? "+" : ""} snow report${summary.total === 1 ? "" : "s"}`;

  if (summary.total === 0) {
    return (
      <p className="text-[13px] text-text-muted">
        No snow reports in the last {summary.windowHours} hours yet.
      </p>
    );
  }

  // Too few reports to show a breakdown without revealing one person's report.
  if (summary.breakdownWithheld) {
    return (
      <p className="text-[13px] text-text-muted">
        <span className="font-semibold text-navy">{reports}</span> in the last {summary.windowHours} hours so far.
        {" "}The breakdown appears once there are {SNOW_CHECKIN_SUMMARY.minReportsForBreakdown} or more.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-[13.5px] text-text-body">
        <span className="font-semibold text-navy">{reports}</span> in the last {summary.windowHours} hours
        {summary.rating.average !== null ? (
          <>
            {" "}
            · average rating <span className="font-semibold text-navy">{summary.rating.average.toFixed(1)}</span>/10
          </>
        ) : null}
      </p>

      {coverTotal > 0 ? (
        <div>
          <h4 className="text-[12px] font-semibold text-text-muted">Snow cover</h4>
          <ul className="mt-1.5 space-y-1.5">
            {SNOW_CHECKIN_COVERS.map((cover) => {
              const count = summary.cover[cover];
              const pct = Math.round((count / coverTotal) * 100);
              return (
                <li key={cover} className="flex items-center gap-2 text-[12.5px] text-text-body">
                  <span className="w-28 shrink-0">{SNOW_CHECKIN_COVER_LABELS[cover]}</span>
                  <span className="h-2 flex-1 overflow-hidden rounded-full bg-cream" aria-hidden="true">
                    <span className={`block h-full rounded-full ${COVER_BAR[cover]}`} style={{ width: `${pct}%` }} />
                  </span>
                  <span className="w-6 shrink-0 text-right tabular-nums">{count}</span>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}

      {surfaces.length > 0 ? (
        <div>
          <h4 className="text-[12px] font-semibold text-text-muted">Surface</h4>
          <ul className="mt-1.5 flex flex-wrap gap-1.5">
            {surfaces.map(({ surface, count }) => (
              <li key={surface} className="rounded-full bg-warm-bg px-3 py-1 text-[12px] font-semibold text-navy">
                {SNOW_CHECKIN_SURFACE_LABELS[surface]} <span className="font-normal text-text-muted">×{count}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
